import { Link } from "react-router-dom";
import { useState } from "react";
import { Plus, Minus, ArrowRight, HelpCircle } from "lucide-react";
import WaveBackground from "@/components/site/WaveBackground";
import useSEO from "@/lib/useSEO";

const faqs = [
  {
    q: "Who does Fenco Digital work with?",
    a: "We work with the right partner for your business — not tied to any single provider. That means we can benchmark the market on your behalf and match you with the sharpest deal for how you actually trade.",
  },
  {
    q: "How quickly can I be up and running?",
    a: "Most clients are taking payments within a few working days. Sign us your details today and you could be accepting contactless debit and credit cards (plus Apple Pay, Google Pay and Samsung Pay) shortly after.",
  },
  {
    q: "Are the card machines secure?",
    a: "Yes — every terminal we set up comes with point-to-point encryption and instant fraud detection. Your customers' card data and your profits stay protected by first-class payment security.",
  },
  {
    q: "What happens if my Wi-Fi drops?",
    a: "The modern terminals we recommend all have built-in 4G connectivity that kicks in automatically if your Wi-Fi disconnects — so you keep taking payments even when the broadband doesn't co-operate.",
  },
  {
    q: "I'm tied into a contract — can I still switch?",
    a: "Often yes. Depending on the partner we match you with, exit fees can be partially or fully covered (subject to your annual card turnover). We'll review your current statement and tell you exactly what you'd save and what's covered.",
  },
  {
    q: "When do I get paid?",
    a: "Next-day settlement — often 7 days a week including weekends and bank holidays — is standard with the modern providers we work with. Funds typically land in your business account by 10–11am the day after the transaction.",
  },
  {
    q: "Will it integrate with my till / EPOS?",
    a: "Almost certainly. The payment providers we partner with connect to all the major EPOS systems — Square, Lightspeed, Toast, Tevalis, Zonal, ICRTouch and many more. Totals fly from the till to the terminal automatically; no re-keying, no mistakes.",
  },
  {
    q: "What about PCI compliance?",
    a: "The providers we work with include guided PCI compliance reporting built into their apps, so the annual obligation takes minutes — not hours of paperwork.",
  },
  {
    q: "Do you cover all of Northern Ireland and the UK?",
    a: "Yes — we're a local Northern Ireland team supporting businesses across NI and the wider UK. On-site setup, remote support, and we're with you on go-live day.",
  },
  {
    q: "Is there a fixed contract?",
    a: "It depends on the partner and product. We only work with providers offering fair, transparent terms with no surprise lock-ins. We'll walk you through the exact contract structure when we send your quote — no small print.",
  },
  {
    q: "Do you build websites and apps too?",
    a: "Yes — alongside card payments and POS, we offer website design and app design. Whether you need a new site to sit alongside your till, or a full mobile app for bookings/loyalty, we can spec, design and build it. See our Website Design and App Design pages for details.",
  },
  {
    q: "Do you charge for the cost comparison?",
    a: "Not a penny. The benchmark is free, line-by-line and there's no obligation to switch. If we can't save you money, we'll say so.",
  },
];

export default function FAQPage() {
  useSEO({
    title: "Frequently Asked Questions — Card Payments, POS, Web & App Design | Fenco Digital",
    description: "Answers to the questions UK business owners ask us most — card machines, fees, payouts, EPOS integrations, PCI compliance, contracts, website and app design.",
    path: "/faq",
  });

  const [open, setOpen] = useState(0);

  return (
    <div data-testid="faq-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <WaveBackground variant="accent" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-6">
            <HelpCircle size={14} className="text-[#FF5A1F]" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF5A1F]">Frequently asked questions</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-gray-900">
            Questions,<br />answered<span className="text-[#FF5A1F]">.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            The things business owners ask us most about card payments, terminals, POS, and web &amp; app design. Anything else? Just{" "}
            <Link to="/contact" className="text-[#FF5A1F] font-semibold hover:underline" data-testid="faq-contact-inline-link">drop us a line</Link>.
          </p>
        </div>
      </section>

      {/* FAQ LIST */}
      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="space-y-3" data-testid="faq-list">
            {faqs.map((f, i) => (
              <div
                key={f.q}
                data-testid={`faq-item-${i}`}
                className="bg-[#F8F9FA] border border-gray-100 rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`faq-item-${i}-toggle`}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-gray-50 transition"
                >
                  <span className="font-display text-base md:text-lg font-bold text-gray-900">{f.q}</span>
                  {open === i ? (
                    <Minus size={20} className="text-[#FF5A1F] shrink-0" />
                  ) : (
                    <Plus size={20} className="text-gray-400 shrink-0" />
                  )}
                </button>
                {open === i && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed" data-testid={`faq-item-${i}-answer`}>{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-[#0a0a0a] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Still got a question<span className="text-[#FF5A1F]">?</span>
          </h2>
          <p className="mt-5 text-lg text-gray-300 max-w-xl mx-auto">
            We're a local Northern Ireland team — message us and you'll hear back from a real human, fast.
          </p>
          <Link
            to="/contact"
            data-testid="faq-final-cta"
            className="mt-9 inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/30"
          >
            Talk to a human <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
